import React, { type JSX } from 'react';
import Link from 'next/link';
import Question from '@monofront/design-system/visuals/Icons/Old/Question';
import Tag from '@monofront/design-system/components/Tag';
import { format } from '@monofront/business/utils';
import { getPercentageChange } from '@monofront/business/utils';
import Popover from '@components/Popover';
import { useTranslations } from 'next-intl';

interface ICTAProps {
  danger?: boolean;
  title: string;
  weight?: string;
  price: number;
  comparedAtPrice: number;
  shouldHidePrice?: boolean;
  ctaButton: JSX.Element;
  isStickyBottomScreen?: boolean;
  isMaxHeight?: boolean;
}

const CTA: React.FC<ICTAProps> = ({
  danger = false,
  title,
  weight,
  price,
  shouldHidePrice,
  comparedAtPrice,
  ctaButton,
  isStickyBottomScreen,
  isMaxHeight,
}) => {
  const tCatalog = useTranslations('Catalog');

  const isPriceDiscounted = comparedAtPrice > 0 && comparedAtPrice !== price;

  return (
    <>
      <div className="container">
        {isStickyBottomScreen ? (
          <div className="product">
            <p className="title">{title}</p>
            {weight ? <p className="weight">{weight}</p> : null}
          </div>
        ) : null}
        {!shouldHidePrice ? (
          <div className="price">
            {isPriceDiscounted ? (
              <div className="line discount-line">
                <span className="not-member-price">
                  {format(comparedAtPrice)}
                </span>

                <div className="tooltip">
                  {tCatalog('referencePrice')}
                  <Popover content={<p>{tCatalog('referencePriceDetails')}</p>}>
                    <div className="tooltip-icon">
                      <Question width={16} height={16} />
                    </div>
                  </Popover>
                </div>
              </div>
            ) : null}

            <div className="line">
              <p className="member-price">{format(price)}</p>
              {isPriceDiscounted ? (
                <span className="economy-percent">
                  <Tag variant="secondary">
                    {-getPercentageChange(comparedAtPrice, price).toFixed(0) +
                      '%'}
                  </Tag>
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <Link href="/account/register/flow" legacyBehavior>
            {/* LF-2940 - LINK SIZE OUT OF DESIGN SYSTEM */}
            <a
              className="tw-lf-link-md tw-text-[22px]"
              rel="nofollow"
              data-testid="CTA-go-to-see-member-price"
            >
              {tCatalog('seeMemberPrice')}
            </a>
          </Link>
        )}
        <div className="buy-cta">{ctaButton}</div>
      </div>
      <style jsx>{`
        .container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-2) 0;
        }

        .product {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: var(--space-4);
        }

        .price {
          display: flex;
          flex-direction: ${isStickyBottomScreen ? 'row' : 'column'};
          gap: ${isStickyBottomScreen ? 'var(--space-4)' : 'var(--space-1)'};
        }

        .member-price {
          font-size: var(--font-size-4);
          font-weight: var(--font-weight-bold);
          line-height: 34px;
          color: ${danger ? 'var(--clr-secondary-1)' : 'inherit'};
          font-family: var(--font-family-1);
        }

        .line {
          display: flex;
          gap: var(--space-4);
          align-items: center;
        }

        .discount-line {
          flex-direction: ${isStickyBottomScreen ? 'row-reverse' : 'row'};
        }

        .not-member-price {
          font-size: var(--font-size-2-5);
          text-decoration: line-through;
          ${danger ? 'color: var(--clr-secondary-1);' : ''}
        }

        .economy-percent > :global(div) {
          font-size: var(--font-size-3);
        }

        .buy-cta {
          margin-left: var(--space-6);
          height: 66px;
          display: flex;
          align-items: center;
        }

        .buy-cta :global(.btn-initial) {
          width: 100%;
        }

        .buy-cta :global(.btn-initial > button) {
          padding: var(--space-3) var(--space-5);
          height: initial;
          line-height: 0;
        }

        .buy-cta :global(button) {
          transition: background 0.5s, border 0.5s;
        }

        .buy-cta :global(.btn-show-count button) {
          padding: 0;
          line-height: initial;
        }

        .product {
          margin-right: var(--space-6);
          overflow: hidden;
        }

        .title {
          font-size: var(--font-size-3);
          font-weight: var(--font-weight-bold);
          line-height: var(--font-size-5);
          white-space: nowrap;
          overflow-x: hidden;
          text-overflow: ellipsis;
        }

        .weight {
          font-size: var(--font-size-2);
          line-height: 40px;
        }

        .tooltip {
          font-size: var(--font-size-1);
          color: var(--black);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-1);
        }

        @media (max-width: 1024px) {
          .buy-cta {
            margin-left: var(--space-2);
          }

          .price {
            display: flex;
            flex-direction: column;
            gap: var(--space-1);
          }

          .discount-line {
            flex-direction: row;
          }
        }

        @media (max-width: 800px) {
          .container {
            height: ${isMaxHeight ? '78px' : '73px'};
          }
          .product {
            display: none;
          }

          .buy-cta {
            margin: 0;
            height: 50px;
          }

          .buy-cta :global(.btn-initial > button) {
            padding: var(--space-2) var(--space-5);
          }

          .member-price {
            font-size: var(--font-size-4);
            line-height: 26px;
          }

          .not-member-price {
            margin-top: var(--space-1);
          }

          .not-member-price,
          .economy-percent > :global(div) {
            font-size: var(--font-size-2);
            line-height: var(--font-size-3);
          }
        }
      `}</style>
    </>
  );
};

export default CTA;
